import { buildContractStatusActions } from "./actions/build-contract-status-actions";

import {
  PermissionsAction,
  PermissionsConfig,
  PermissionsPosModule,
  PermissionsScope
} from "@/common/types/permissions.types";
import type {
  ApprovalFlow,
  Contract
} from "@/components/Contracts/contract.interface";
import { ContractStatus } from "@/modules/Contracts/enums";
import { IStep } from "@/modules/Terms/interfaces/ITerm.interface";

interface IBuildMenu {
  contract: Contract;
  approvalFlow?: ApprovalFlow;
  permissions: PermissionsConfig;
  nextStep: IStep;
  setIsVisible: (isVisible: boolean) => void;
  setIsContractOccurrencesDrawerVisible: (
    isContractOccurrencesDrawerVisible: boolean
  ) => void;
  action: string;
  toggleActionModal: () => void;
  setAction: (action: string) => void;
  setActionMessage: (actionMessage: string) => void;
  contractId: string;
  downloadContract?: (data?: { signed?: boolean }) => Promise<void>;
  isButtonDisabled?: boolean;
}

export const buildMenu = ({
  contract,
  approvalFlow,
  permissions,
  nextStep,
  setIsVisible,
  setIsContractOccurrencesDrawerVisible,
  action,
  toggleActionModal,
  setAction,
  setActionMessage,
  isButtonDisabled,
  downloadContract
}: IBuildMenu) => {
  const status = buildContractStatusActions({
    contract,
    approvalFlow,
    permissions,
    nextStep,
    isButtonDisabled,
    action,
    toggleActionModal,
    setAction,
    setActionMessage
  });

  const canSendDocuments = contract => {
    if (!approvalFlow?.steps) return false;

    const lastStepToSendDocument = approvalFlow.steps.find(
      step => step.internalName === ContractStatus.SERVICE_DELIVERED
    );

    return (
      contract.currentStep?.order >= 10 &&
      contract.currentStep?.order <= lastStepToSendDocument?.order
    );
  };

  const downloadOptions = [
    {
      key: "Imprimir contrato original",
      text: "Documento Original",
      onClick: () => void downloadContract(),
      id: `original`
    },
    {
      key: "Imprimir contrato assinado",
      text: "Documento Assinado",
      onClick: () => void downloadContract({ signed: true }),
      id: `signed`
    }
  ];

  const isDone = contract.currentStep.internalName === ContractStatus.DONE;
  const isPrintDisabled = !contract.pdfPath;
  const isChangeStatusDisabled = status?.length === 0;

  const menu = [
    {
      id: "button-print",
      key: "Imprimir",
      text: "Imprimir",
      iconProps: {
        iconName: "Print"
      },
      subMenuProps: isDone ? { items: downloadOptions } : undefined,
      onClick: isDone ? undefined : () => void downloadContract(),
      disabled: isPrintDisabled
    },
    {
      id: "button-change-status",
      key: "status",
      text: "Alterar status",
      iconProps: {
        iconName: "Sync"
      },
      subMenuProps: {
        items: status
      },
      disabled: isChangeStatusDisabled
    },
    {
      id: "button-attach-documents",
      key: "Anexar Documento(s)",
      text: "Anexar Documento(s)",
      iconProps: {
        iconName: "DocumentSet"
      },
      disabled: !canSendDocuments(contract),
      onClick: () => setIsVisible(true)
    }
  ];

  const canReadContractOccurrences = permissions[PermissionsScope.POS]?.[
    PermissionsPosModule.CONTRACTS
  ]?.some(
    contractAction =>
      contractAction === PermissionsAction.ALL ||
      contractAction === "contract-occurrences"
  );

  const contractHistoryButton = {
    id: "button-contract-history",
    key: "contractOccurrences",
    text: "Histórico do contrato",
    iconProps: {
      iconName: "FullHistory"
    },
    onClick: () => setIsContractOccurrencesDrawerVisible(true),
    disabled: false
  };

  if (canReadContractOccurrences) {
    menu.push(contractHistoryButton);
  }

  return menu;
};
